import * as React from "react";
import { useState, useEffect, useRef } from "react";
import { ChevronDown, Folder, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { WorkPillExecution } from "../../../types/navigationTypes";

interface ProjectSelectorTriggerProps {
  selectedProject?: WorkPillExecution;
  onClick: () => void;
  isOpen: boolean;
  onRename?: (newName: string) => Promise<boolean>;
}

export const ProjectSelectorTrigger: React.FC<ProjectSelectorTriggerProps> = ({
  selectedProject,
  onClick,
  isOpen,
  onRename
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [tempName, setTempName] = useState(selectedProject?.name || "");
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (selectedProject?.name) {
      setTempName(selectedProject.name);
    }
  }, [selectedProject?.name]);

  const handleRenameClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsEditing(true);
  };

  const handleSave = async () => {
    if (tempName.trim() && tempName !== selectedProject?.name && onRename) {
      const success = await onRename(tempName.trim());
      if (!success) {
        setTempName(selectedProject?.name || "");
      }
    } else {
      setTempName(selectedProject?.name || "");
    }
    setIsEditing(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSave();
    } else if (e.key === 'Escape') {
      setTempName(selectedProject?.name || "");
      setIsEditing(false);
    }
  };

  if (isEditing) {
    return (
      <div 
        className="flex flex-col items-start px-3 h-9 justify-center border border-primary/30 bg-white rounded-md max-w-[240px] shadow-sm"
        onClick={(e) => e.stopPropagation()}
      >
        <span className="text-[10px] font-medium text-primary leading-none mb-0.5">
          Renaming Project
        </span>
        <input
          ref={inputRef}
          autoFocus
          className="text-sm font-semibold text-slate-900 bg-transparent border-none outline-none w-full p-0 h-4 leading-none"
          value={tempName}
          onChange={(e) => setTempName(e.target.value)}
          onBlur={handleSave}
          onKeyDown={handleKeyDown}
        />
      </div>
    );
  }

  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={onClick}
      className={cn(
        "h-9 px-3 gap-2 border border-transparent hover:border-border hover:bg-slate-100 transition-all max-w-[240px] group",
        isOpen && "bg-slate-100 border-border"
      )}
    >
      <div className={cn(
        "flex items-center justify-center w-5 h-5 rounded shrink-0",
        selectedProject ? "text-gray-600 bg-gray-50" : "text-slate-500 bg-slate-100"
      )}>
        <Folder className="w-3.5 h-3.5" />
      </div>

      <div className="flex flex-col items-start overflow-hidden text-left flex-1">
        <span className="text-xs font-medium text-slate-500 leading-none mb-0.5">
          {selectedProject ? "Project" : "WorkOps"}
        </span>
        <div className="flex items-center gap-1 w-full overflow-hidden">
          <span className={cn(
            "text-sm font-semibold truncate leading-none",
            selectedProject ? "text-slate-900" : "text-slate-600"
          )}>
            {selectedProject ? selectedProject.name : "Select Project"}
          </span>
          {selectedProject && (
            <Pencil 
              className="w-3 h-3 text-slate-400 opacity-0 group-hover:opacity-100 transition-opacity shrink-0 hover:text-primary" 
              onClick={handleRenameClick}
            />
          )}
        </div>
      </div>

      <ChevronDown className={cn(
        "w-3 h-3 text-slate-400 shrink-0 ml-1 transition-transform",
        isOpen && "rotate-180"
      )} />
    </Button>
  );
};
